import { memo, useState } from "react";
import { ChevronDown, ChevronRight, Zap } from "lucide-react";
import { SkillResultMarkdown } from "@/components/organisms/studio/SkillResultMarkdown";
import { type ToolResultData, toolResultOutcome } from "@/services/testing";
import type { RenderedMessage } from "@/stores/testingStore";
import { formatTokens } from "@/utils/testing/planDisplay";

/**
 * The agent's prose reply for one message.
 *
 * Deliberately NOT a bubble. The agent's answer is the main content of the column, so giving it a
 * tinted box (as the old shared ChatBubble did for both roles) put a border around 90% of the thread
 * and left nothing for the eye to latch onto. The user's message is the bubble; the answer is the
 * page. This is the same asymmetry Weave uses.
 */
export const AssistantMessage = memo(function AssistantMessage({ message }: { message: RenderedMessage }) {
	const [toolOpen, setToolOpen] = useState(false);
	const usage = message.metadata?.usage as { total?: number; calls?: number } | undefined;
	const hasBody = Boolean(message.content?.trim()) || Boolean(message.tool);
	if (!hasBody && !usage?.total) return null;

	return (
		<div className="tst-prose min-w-0 text-[13px] leading-relaxed text-foreground">
			{message.tool && <ToolDisclosure message={message} open={toolOpen} onToggle={setToolOpen} />}

			{message.content?.trim() && (
				<SkillResultMarkdown
					content={message.content}
					isStreaming={false}
					className="text-[13px] leading-relaxed"
				/>
			)}

			{usage?.total ? (
				<div
					className="mt-1.5 inline-flex items-center gap-1 text-[11px] text-muted-foreground/70"
					title={`Agent used ~${usage.total} tokens across ${usage.calls ?? 0} model call(s) this turn`}
				>
					<Zap className="h-3 w-3" />
					{formatTokens(usage.total)} tokens
					{usage.calls ? ` · ${usage.calls} calls` : ""}
				</div>
			) : null}
		</div>
	);
});

/**
 * The tool call behind a reply, collapsed.
 *
 * `ok: true` only means the call RETURNED. A payload saying the tool was unavailable, that the agent
 * abstained, or that the FLS graph is empty grounded nothing — rendering it green would claim
 * evidence that was never gathered.
 */
function ToolDisclosure({
	message,
	open,
	onToggle,
}: {
	message: RenderedMessage;
	open: boolean;
	onToggle: (next: boolean) => void;
}) {
	const tool = message.tool!;
	const outcome = toolResultOutcome(tool.ok !== false, tool.data, tool.error);
	const payload = (
		tool.data && typeof tool.data === "object" && !Array.isArray(tool.data) ? tool.data : {}
	) as ToolResultData;

	return (
		<>
			<button
				type="button"
				onClick={() => onToggle(!open)}
				aria-expanded={open}
				className="tst-interactive mb-1 flex items-center gap-1 rounded font-mono text-[11px] text-muted-foreground hover:text-foreground"
				title={outcome.reason}
			>
				{open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
				tool: {tool.name}
				{tool.ok === undefined ? null : outcome.status === "error" ? (
					<span className="text-destructive-strong">· failed</span>
				) : outcome.status === "warning" ? (
					payload.abstained === true ? (
						// Not an error and not evidence: the org simply had no match.
						<span className="text-muted-foreground">· no match in org</span>
					) : (
						<span className="text-warning-strong">· unavailable</span>
					)
				) : (
					<span className="text-success-strong">· ok</span>
				)}
			</button>
			{open && (
				<pre className="mb-2 max-h-48 overflow-auto rounded border border-border bg-card p-2 text-[11px] text-muted-foreground">
					{JSON.stringify({ args: tool.args, result: tool.data, error: tool.error }, null, 2)}
				</pre>
			)}
		</>
	);
}
